import { useState } from "react";

import dynamic from "next/dynamic";
import Image from "next/image";

import { LOCALE_CODES } from "@repo/locale";

import { FlightSpinLoader } from "@repo/ui/components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import { cn } from "@repo/ui/lib/utils";
import type { SiteSettings } from "@repo/ui/types";
import type { BaseTabContent, SmartSearchInsuranceTab } from "@repo/ui/types/cms/home";
import type { GroupedStations } from "@repo/ui/types/stations";

import { useLocale } from "@repo/store/slices";

import { useThirdParties } from "@/app/[country]/[lang]/(home)/ThirdPartiesProvider";
import { ThirdPartyIframe } from "@/features/home/components/HomePageWidget/SearchSmart/thirdPartyIframe";
import FlightsWidget from "./Flights";

const InsuranceWidget = dynamic(
  () => import("@/features/home/components/HomePageWidget/SearchSmart/Insurance"),
  { loading: () => <div className="animate-pulse rounded bg-gray-200 p-4" /> },
);

type SearchSmartProps = {
  destinations: GroupedStations;
  siteSettings: SiteSettings;
  searchSmartTabs: BaseTabContent[];
};

const SearchSmart = ({ destinations, siteSettings, searchSmartTabs }: SearchSmartProps) => {
  const locale = useLocale();
  const { chooseToHide, insuranceTab, insuranceButtonUrls } = siteSettings;
  const [selectedTab, setSelectedTab] = useState<string>(searchSmartTabs[0]!.type);
  const { data } = useThirdParties();
  const thirdParties = data?.page.sections[0];
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div
      className="relative w-full bg-white p-4 max-sm:rounded-tr-lg max-sm:rounded-b-lg"
      data-testid="search-smart-tab"
    >
      {isLoading && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center rounded-lg bg-white/70">
          <FlightSpinLoader size="small" />
        </div>
      )}
      <Tabs
        defaultValue={selectedTab}
        className="w-full"
        onValueChange={(value) => {
          setSelectedTab(value);
        }}
      >
        <div className="w-full md:w-auto">
          <TabsList className="mb-2 flex h-auto w-full flex-wrap items-center justify-start gap-2 bg-transparent p-0 md:w-auto">
            {searchSmartTabs.map((tab) => {
              if (
                tab.codename === "searchsmart_hotel_tab" &&
                chooseToHide.includes("hotel_search")
              ) {
                return null;
              } else if (
                tab.codename === "smartsearch_cars_tab" &&
                chooseToHide.includes("car_search")
              ) {
                return null;
              } else if (
                tab.codename === "smartsearch_transfer" &&
                chooseToHide.includes("transfer_search")
              ) {
                return null;
              } else if (tab.codename === "smartsearch_insurance") {
                if (
                  (locale === LOCALE_CODES.Argentina &&
                    (!insuranceTab.includes("show_insurance_tab_for_argentina") ||
                      !insuranceButtonUrls.insurance_argentina_button_url)) ||
                  (locale === LOCALE_CODES.Chile &&
                    (!insuranceTab.includes("show_insurance_tab_for_chile") ||
                      !insuranceButtonUrls.insurance_chile_button_url)) ||
                  (locale === LOCALE_CODES.Peru &&
                    (!insuranceTab.includes("show_insurance_tab_for_peru") ||
                      !insuranceButtonUrls.insurance_peru_button_url)) ||
                  (locale === LOCALE_CODES.Ecuador &&
                    (!insuranceTab.includes("show_insurance_tab_for_ecuador") ||
                      !insuranceButtonUrls.insurance_ecuador_button_url)) ||
                  (locale === LOCALE_CODES.Brazil &&
                    (!insuranceTab.includes("show_insurance_tab_for_brazil") ||
                      !insuranceButtonUrls.insurance_brazil_button_url)) ||
                  (locale === LOCALE_CODES.Paraguay &&
                    (!insuranceTab.includes("show_insurance_tab_for_paraguay") ||
                      !insuranceButtonUrls.insurance_paraguay_button_url)) ||
                  (locale === LOCALE_CODES.Uruguay &&
                    (!insuranceTab.includes("show_insurance_tab_for_uruguay") ||
                      !insuranceButtonUrls.insurance_uruguay_button_url)) ||
                  (locale === LOCALE_CODES.USA &&
                    (!insuranceTab.includes("show_insurance_tab_for_english") ||
                      !insuranceButtonUrls.insurance_english_button_url)) ||
                  (locale === LOCALE_CODES.Colombia &&
                    (!insuranceTab.includes("show_insurance_tab_for_colombia") ||
                      !insuranceButtonUrls.insurance_colombia_button_url)) ||
                  (locale === LOCALE_CODES.DominicanRepublic &&
                    (!insuranceTab.includes("show_insurance_tab_for_dominican_r_") ||
                      !insuranceButtonUrls.insurance_dominican_r__button_url))
                ) {
                  return null;
                }
              }

              return (
                <TabsTrigger
                  key={tab.tabName}
                  value={tab.type}
                  className={cn(
                    "flex w-auto flex-none shrink-0 cursor-pointer items-center justify-center gap-1 text-xs sm:text-sm",
                    "text-brand-gray rounded-md bg-gray-100 px-2 py-1.5 transition-all duration-200 ease-in-out md:px-4 md:py-3",
                    "hover:text-brand-blue hover:bg-gray-300 data-[state=active]:bg-brand-cyan data-[state=active]:font-black data-[state=active]:text-white",
                  )}
                  data-testid={`search-${String(tab.tabName)?.toLowerCase()}-tab-btn`}
                >
                  <Image
                    src={selectedTab === tab.type ? tab.activeTabIconUrl : tab.inactiveTabIconUrl}
                    alt={tab.tabName}
                    width={16}
                    height={16}
                    className="shrink-0"
                  />
                  <span className="whitespace-nowrap">{tab.tabName}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {searchSmartTabs.map((tab) => (
          <TabsContent key={tab.type} value={tab.type}>
            {tab.type === "smartsearch_flight" && (
              <FlightsWidget destinations={destinations} setIsLoading={setIsLoading} />
            )}

            {tab.type === "smartsearch_hotel_tab" && !chooseToHide.includes("hotel_search") && (
              <ThirdPartyIframe
                html={thirdParties?.iframes.hotels}
                testId="search-smart-hotels"
                contentClassName="max-md:w-full max-md:max-w-full max-md:overflow-x-auto max-md:[&_*]:max-w-full! max-md:[&_iframe]:w-full!"
              />
            )}

            {tab.type === "smartsearch_cars_tab" && !chooseToHide.includes("car_search") && (
              <ThirdPartyIframe html={thirdParties?.iframes.cars} testId="search-smart-cars" />
            )}

            {tab.type === "smartsearch_transfer_tab" &&
              !chooseToHide.includes("transfer_search") && (
                <ThirdPartyIframe
                  html={thirdParties?.iframes.transport}
                  testId="search-smart-transfers"
                />
              )}

            {tab.type === "smartsearch_insurance_tab" && (
              <InsuranceWidget content={tab as SmartSearchInsuranceTab} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default SearchSmart;
